import { currentLocale } from '@/lib/locale';
import { useEffect, useMemo, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useInternalAccessLog } from '@/hooks/useInternalPanel';
import { InternalTable, EmptyState, LoaderBlock, fmtMs, todayISO } from './shared';
import { useTranslation } from 'react-i18next';

type LogEvent = {
  eventId: string;
  timestamp: string;
  userEmail?: string;
  path: string;
  module: string;
  type: string;
  durationMs?: number;
  ok: boolean;
  errorCode?: string;
};

/**
 * Log crudo de accesos, un día por vez, filtrable por usuario y módulo.
 * La paginación acumula páginas con el cursor del backend; cambiar cualquier
 * filtro resetea la acumulación.
 */
export function AccessLogTab() {
  const { t } = useTranslation();
  const [day, setDay] = useState(todayISO());
  const [userEmail, setUserEmail] = useState('');
  const [module, setModule] = useState('');
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [accum, setAccum] = useState<LogEvent[]>([]);

  const filterKey = useMemo(() => `${day}|${userEmail}|${module}`, [day, userEmail, module]);

  const query = useInternalAccessLog({
    day,
    userEmail: userEmail.includes('@') ? userEmail.trim() : undefined,
    module: module.trim() || undefined,
    cursor,
    limit: 50,
  });

  // Reset de acumulación al cambiar filtros
  useEffect(() => {
    setCursor(undefined);
    setAccum([]);
  }, [filterKey]);

  // Acumular la página recibida (sin duplicar por eventId)
  useEffect(() => {
    if (!query.data) return;
    setAccum((prev) => {
      const seen = new Set(prev.map((e) => e.eventId));
      const fresh = (query.data.events as LogEvent[]).filter((e) => !seen.has(e.eventId));
      return [...prev, ...fresh];
    });
  }, [query.data]);

  const nextCursor = query.data?.nextCursor ?? null;

  return (
    <div className="space-y-4">
      <Card className="p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Día</label>
            <input
              type="date"
              value={day}
              max={todayISO()}
              onChange={(e) => setDay(e.target.value)}
              className="h-8 text-sm px-2 rounded-md border bg-background text-foreground"
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground block mb-1">{t('internal.accesslogtab.usuario_email_exacto')}</label>
            <Input
              value={userEmail}
              onChange={(e) => setUserEmail(e.target.value)}
              placeholder="alguien@winclap.com"
              className="h-8 w-56 text-sm"
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground block mb-1">{t('internal.accesslogtab.modulo')}</label>
            <Input
              value={module}
              onChange={(e) => setModule(e.target.value)}
              // i18n-ok: sintaxis del filtro
              placeholder="inventory, brain, page:admin…"
              className="h-8 w-48 text-sm"
            />
          </div>
          {(userEmail || module) && (
            <Button variant="ghost" size="sm" className="h-8" onClick={() => { setUserEmail(''); setModule(''); }}>
              Limpiar
            </Button>
          )}
        </div>
      </Card>

      {query.isLoading && accum.length === 0 ? (
        <LoaderBlock />
      ) : accum.length === 0 ? (
        <EmptyState
          title={t('internal.accesslogtab.sin_eventos_para_estos_filtros')}
          description={t('internal.accesslogtab.la_telemetria_acumula_desde_el_deploy')}
        />
      ) : (
        <Card className="p-5">
          <InternalTable
            rows={accum}
            rowKey={(e) => e.eventId}
            columns={[
              { key: 'time', label: t('internal.accesslogtab.hora'), className: 'text-xs text-muted-foreground whitespace-nowrap', render: (e) => new Date(e.timestamp).toLocaleTimeString(currentLocale()) },
              { key: 'user', label: t('internal.accesslogtab.usuario'), className: 'truncate max-w-[200px]', render: (e) => e.userEmail },
              { key: 'path', label: t('internal.accesslogtab.que'), className: 'font-mono text-xs truncate max-w-[260px]', render: (e) => e.path },
              { key: 'type', label: t('internal.accesslogtab.tipo'), className: 'text-xs text-muted-foreground', render: (e) => e.type },
              { key: 'duration', label: t('internal.accesslogtab.duracion'), align: 'right', className: 'text-xs', render: (e) => (e.type === 'page' ? '—' : fmtMs(e.durationMs)) },
              {
                key: 'status', label: t('internal.accesslogtab.estado'), align: 'right',
                render: (e) => e.ok
                  ? <span className="text-emerald-600 dark:text-emerald-400 text-xs">OK</span>
                  : <span className="text-rose-600 dark:text-rose-400 text-xs font-medium">{e.errorCode ?? 'ERROR'}</span>,
              },
            ]}
          />
          <div className="flex items-center justify-between mt-3">
            <span className="text-xs text-muted-foreground">{accum.length} eventos</span>
            {nextCursor && (
              <Button variant="outline" size="sm" disabled={query.isFetching} onClick={() => setCursor(nextCursor)}>
                {query.isFetching ? t('internal.accesslogtab.cargando') : t('internal.accesslogtab.cargar_mas')}
              </Button>
            )}
          </div>
        </Card>
      )}
    </div>
  );
}
